import { useEffect, useMemo, useState } from "react";
import { setCover } from "../api.js";
import Placeholder from "./Placeholder.jsx";
import Lightbox from "./Lightbox.jsx";
import Upload from "./Upload.jsx";

export default function Detail({ visit, prev, next, onClose, onNav, photos, coverPhotoId, onUploaded, onCoverChanged }) {
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [pendingCoverId, setPendingCoverId] = useState(null);

  async function handleSetCover(photoId, e) {
    e?.stopPropagation();
    if (pendingCoverId) return;
    setPendingCoverId(photoId);
    try {
      await setCover(visit.id, photoId);
      onCoverChanged?.(photoId);
    } catch (err) {
      alert(`設定封面失敗：${err.message}`);
    } finally {
      setPendingCoverId(null);
    }
  }

  // 排序：EXIF 拍攝時間 → 上傳時間
  const sortedPhotos = useMemo(() => {
    return [...(photos || [])].sort((a, b) => {
      const aT = a.exifDate ? new Date(a.exifDate).getTime() : new Date(a.uploadedAt).getTime();
      const bT = b.exifDate ? new Date(b.exifDate).getTime() : new Date(b.uploadedAt).getTime();
      return aT - bT;
    });
  }, [photos]);

  useEffect(() => {
    const h = (e) => { if (e.key === "Escape" && lightboxIdx === null && !uploadOpen) onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose, lightboxIdx, uploadOpen]);

  const lightboxPhoto = lightboxIdx !== null ? sortedPhotos[lightboxIdx] : null;

  return (
    <div className="det-overlay">
      <div className="detB-top">
        <button className="detB-back" onClick={onClose}>← 返回索引</button>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <button className="upload-trigger" onClick={() => setUploadOpen(true)}>
            📷 上傳照片
          </button>
          <img src="/ncku-emba-logo.jpeg" alt="NCKU EMBA" />
          <div style={{ borderLeft: "1px solid #e3e7ee", paddingLeft: 14, lineHeight: 1.2 }}>
            <div style={{ fontFamily: "JetBrains Mono, monospace", fontSize: 10, letterSpacing: "0.22em", color: "#b08a4a", textTransform: "uppercase" }}>CLASS OF E118</div>
            <div style={{ fontFamily: "Playfair Display, serif", fontSize: 16, fontWeight: 500, color: "#0e1a2b", marginTop: 2 }}>EMBA 第 E118 級</div>
          </div>
        </div>
      </div>

      <section className="detB-hero">
        <div>
          <div className="crumbs">Field Visit · {visit.dateLabel} · {visit.location}</div>
          <h1>{visit.companyShort}</h1>
          <div className="sub">{visit.company}</div>
        </div>
        <div className="detB-meta">
          <div className="detB-meta-cell">
            <div className="lbl">Date</div>
            <div className="val">{visit.dateLabel}</div>
          </div>
          <div className="detB-meta-cell">
            <div className="lbl">Location</div>
            <div className="val">{visit.location}</div>
          </div>
          <div className="detB-meta-cell">
            <div className="lbl">Host</div>
            <div className="val">{visit.host}</div>
          </div>
          <div className="detB-meta-cell">
            <div className="lbl">Photographs</div>
            <div className="val">{sortedPhotos.length}</div>
          </div>
        </div>
      </section>

      {sortedPhotos.length > 0 ? (
        <div className="detB-cover">
          {(() => {
            const cover = (coverPhotoId && sortedPhotos.find((p) => p.id === coverPhotoId)) || sortedPhotos[0];
            // 不用 inline objectFit:cover — CSS 會用 contain 讓全圖完整顯示
            return (
              <img src={cover.medium} alt="封面" referrerPolicy="no-referrer" />
            );
          })()}
        </div>
      ) : (
        <div className="detB-cover">
          <Placeholder kind={visit.cover} label={`${visit.companyShort} · 尚未上傳照片 · 1600×900`} />
        </div>
      )}

      <section className="detB-body">
        <aside className="detB-side">
          <div className="ix-mark">Industries</div>
          <div className="tags">
            {visit.industries.map((t) => <span className="tag" key={t}>{t}</span>)}
          </div>
        </aside>

        <div className="detB-main">
          <h3>Overview</h3>
          <p className="lede">{visit.summary}</p>

          <h3>Highlights</h3>
          <div className="hl-list">
            {visit.highlights.map((h, i) => (
              <div className="hl" key={i}>
                <span className="num">{String(i + 1).padStart(2, "0")}</span>
                <span className="text">{h}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="detB-gallery">
        <div className="detB-gallery-head">
          <h3>Photo Gallery</h3>
          <span className="meta">
            {sortedPhotos.length > 0 ? `${sortedPhotos.length} photographs` : "尚未上傳"}
          </span>
        </div>
        {sortedPhotos.length === 0 ? (
          <div className="gallery-empty">
            <p>還沒有照片。<button className="link-btn" onClick={() => setUploadOpen(true)}>上傳第一張</button></p>
          </div>
        ) : (
          <div className="detB-gallery-grid">
            {sortedPhotos.map((p, i) => {
              const isCover = p.id === coverPhotoId;
              return (
                <button
                  className={`gitem ${isCover ? "is-cover" : ""}`}
                  key={p.id}
                  onClick={() => setLightboxIdx(i)}
                  aria-label={`第 ${i + 1} 張`}
                >
                  <img
                    src={p.thumb}
                    alt={p.originalName || p.id}
                    loading="lazy"
                    referrerPolicy="no-referrer"
                  />
                  <span className="gnum">{String(i + 1).padStart(2, "0")}</span>
                  {isCover && <span className="gnum-cover">★ 封面</span>}
                </button>
              );
            })}
          </div>
        )}
      </section>

      <nav className="detB-nav">
        <button onClick={() => prev && onNav(prev.id)} disabled={!prev}>
          <span className="arrow">← Previous</span>
          <span className="name">{prev ? prev.companyShort : "已是最新"}</span>
        </button>
        <button className="right" onClick={() => next && onNav(next.id)} disabled={!next}>
          <span className="arrow">Next →</span>
          <span className="name">{next ? next.companyShort : "已是最早"}</span>
        </button>
      </nav>

      <div style={{ height: 80 }} />

      {lightboxPhoto && (
        <Lightbox
          photo={lightboxPhoto}
          onClose={() => setLightboxIdx(null)}
          onPrev={() => setLightboxIdx((i) => Math.max(0, i - 1))}
          onNext={() => setLightboxIdx((i) => Math.min(sortedPhotos.length - 1, i + 1))}
          hasPrev={lightboxIdx > 0}
          hasNext={lightboxIdx < sortedPhotos.length - 1}
          isCover={lightboxPhoto.id === coverPhotoId}
          isCoverPending={lightboxPhoto.id === pendingCoverId}
          onSetCover={(e) => handleSetCover(lightboxPhoto.id, e)}
        />
      )}

      <Upload
        visit={visit}
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onUploaded={onUploaded}
      />
    </div>
  );
}
